import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const defaultCtx = {
  routeStops: undefined,
  followUpCount: undefined,
  workerSurface: undefined,
  setWorkerAiSurface: () => {},
  clearWorkerAiSurface: () => {},
};

const AiAssistantWorkerSurfaceContext = createContext(defaultCtx);

/**
 * Worker dashboard is a SPA on `/dashboard`; the AI assistant in App.js cannot infer
 * route vs dashboard tab from the URL. WorkerHome publishes surface hints here.
 */
export function AiAssistantWorkerSurfaceProvider({ children }) {
  const [routeStops, setRouteStops] = useState(undefined);
  const [followUpCount, setFollowUpCount] = useState(undefined);
  const [workerSurface, setWorkerSurface] = useState(undefined);

  const setWorkerAiSurface = useCallback((patch) => {
    if (patch == null) return;
    if ('routeStops' in patch) setRouteStops(patch.routeStops);
    if ('followUpCount' in patch) setFollowUpCount(patch.followUpCount);
    if ('workerSurface' in patch) setWorkerSurface(patch.workerSurface);
  }, []);

  const clearWorkerAiSurface = useCallback(() => {
    setRouteStops(undefined);
    setFollowUpCount(undefined);
    setWorkerSurface(undefined);
  }, []);

  const value = useMemo(
    () => ({
      routeStops,
      followUpCount,
      workerSurface,
      setWorkerAiSurface,
      clearWorkerAiSurface,
    }),
    [routeStops, followUpCount, workerSurface, setWorkerAiSurface, clearWorkerAiSurface],
  );

  return (
    <AiAssistantWorkerSurfaceContext.Provider value={value}>
      {children}
    </AiAssistantWorkerSurfaceContext.Provider>
  );
}

export function useAiAssistantWorkerSurface() {
  return useContext(AiAssistantWorkerSurfaceContext);
}
